import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { Button, ShellHeader } from '@coordio/ui';
import { InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';

import { agentService } from '../services/agentService';
import { AttachedAgentsPanel } from '../components/orchestrator/AttachedAgentsPanel';
import { OrchestratorChat } from '../components/orchestrator/OrchestratorChat';
import type { AgentDefinitionDto } from '../types/agent';

export const OrchestratorRunPage = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<AgentDefinitionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!agentId) return;
    setLoading(true);
    agentService
      .getAgent(agentId)
      .then(setAgent)
      .catch((err) => setError(err.message || 'Failed to load agent'))
      .finally(() => setLoading(false));
  }, [agentId]);

  if (loading) {
    return (
      <div className="orchestrator-run" data-testid="orchestrator-run-detail-page">
        <ShellHeader title="Orchestrator" description="Loading agent…" />
        <PageContainer>
          <LoadingSkeleton rows={6} data-testid="orchestrator-run-detail-loading" />
        </PageContainer>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="orchestrator-run" data-testid="orchestrator-run-detail-page">
        <ShellHeader title="Orchestrator" description="Error loading agent." />
        <PageContainer>
          <InlineErrorPanel message={error || 'Agent not found'} data-testid="orchestrator-run-detail-error" />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="orchestrator-run" data-testid="orchestrator-run-detail-page">
      <ShellHeader
        title={agent.name}
        description={`${agent.slug} · Multi-Agent Orchestrator`}
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => navigate('/orchestrator')}
            data-testid="back-orchestrator-run-btn"
          >
            <ArrowLeft size={14} aria-hidden />
            Back
          </Button>
        }
      />

      {/* Fills remaining shell height under the header — see index.css `.orchestrator-run` */}
      <div className="flex min-h-0 flex-1 overflow-hidden border-t border-[var(--color-border-default)]">
        <aside
          className="flex w-[300px] shrink-0 flex-col overflow-hidden border-r border-[var(--color-border-default)] bg-[var(--color-bg-inset)] p-3"
          data-testid="orchestrator-run-rail"
        >
          <AttachedAgentsPanel parentAgentId={agent.id} data-testid="orchestrator-run-section-agents" />
        </aside>

        <section className="min-h-0 min-w-0 flex-1 overflow-hidden bg-[var(--color-bg-surface)]">
          <OrchestratorChat agentId={agent.id} agentName={agent.name} />
        </section>
      </div>
    </div>
  );
};
